import { ComponentProps, useCallback, useMemo, useState } from "react";
import BonusCreateModal from "../components/editors/payroll-breakdown/BonusCreateModal";
import { FormData } from "../components/editors/payroll-breakdown/PayrollBreakdownEditor";
import { UseFieldArrayAppend, UseFieldArrayRemove, UseFieldArrayUpdate } from "react-hook-form";
import { SalaryDeductionType } from "../__generated__/graphql";
import CreateEffectiveFeeModal from "../components/editors/payroll-breakdown/CreateEffectiveFeeModal";
import AbsenceCreateModal from "../components/editors/payroll-breakdown/AbsenceCreateModal";
import OvertimePaidCreateModal from "../components/editors/payroll-breakdown/OvertimePaidCreateModal";
import AuxiliarySalaryCreateModal from "../components/editors/payroll-breakdown/AuxiliarySalaryCreateModal";

type FormDataArrayFields = "bonuses" | "effectiveFees" | "absences" | "auxiliarySalaries";

type FormDataArrayFieldWithIndex<T extends FormDataArrayFields> = FormData[T][number] & {
    index: number;
};

type FormDataArrayFieldFunctions<T extends FormDataArrayFields> = {
    append: UseFieldArrayAppend<FormData, T>;
    update: UseFieldArrayUpdate<FormData, T>;
    remove: UseFieldArrayRemove;
    arrayLength: number;
};

export type UsePayrollBreakdownExtrasProps = {
    absences?: FormDataArrayFieldFunctions<"absences"> & {
        disabled?: boolean;
        minDate?: Date;
        maxDate?: Date;
    };
    bonus?: FormDataArrayFieldFunctions<"bonuses"> & {
        clientDeductions: SalaryDeductionType[];
    };
    effectiveFees?: FormDataArrayFieldFunctions<"effectiveFees">;
    auxiliarySalaries?: FormDataArrayFieldFunctions<"auxiliarySalaries">;
    overtime?: Omit<ComponentProps<typeof OvertimePaidCreateModal>, "visible" | "onClose">;
};

export const usePayrollBreakdownExtras = ({
    bonus,
    effectiveFees,
    auxiliarySalaries,
    overtime,
    absences,
}: UsePayrollBreakdownExtrasProps) => {
    /*
     ** Bonus
     */
    const {
        isModalVisible: isBonusModalVisible,
        openModal: openBonusModal,
        closeModal: closeBonusModal,
        initialData: initialBonusData,
    } = useExtraModalState<FormDataArrayFieldWithIndex<"bonuses">>();

    const bonusCreateModal = useMemo(
        () =>
            bonus && (
                <BonusCreateModal
                    visible={isBonusModalVisible}
                    initialData={initialBonusData}
                    onClose={closeBonusModal}
                    onCreate={(a) => {
                        closeBonusModal();
                        if (!initialBonusData) {
                            bonus.append(a);
                        } else {
                            bonus.update(initialBonusData.index, a);
                        }
                    }}
                    clientDeductions={bonus.clientDeductions}
                />
            ),
        [bonus, closeBonusModal, initialBonusData, isBonusModalVisible],
    );

    /*
     ** Effective fees
     */
    const {
        isModalVisible: isEffectiveFeesModalVisible,
        openModal: openEffectiveFeesModal,
        closeModal: closeEffectiveFeesModal,
        initialData: initialEffectiveFeesData,
    } = useExtraModalState<FormDataArrayFieldWithIndex<"effectiveFees">>();

    const effectiveFeesModal = useMemo(
        () =>
            effectiveFees && (
                <CreateEffectiveFeeModal
                    visible={isEffectiveFeesModalVisible}
                    initialData={initialEffectiveFeesData}
                    onClose={closeEffectiveFeesModal}
                    onCreate={(a) => {
                        closeEffectiveFeesModal();
                        if (!initialEffectiveFeesData) {
                            effectiveFees.append(a);
                        } else {
                            effectiveFees.update(initialEffectiveFeesData.index, a);
                        }
                    }}
                />
            ),
        [
            closeEffectiveFeesModal,
            effectiveFees,
            initialEffectiveFeesData,
            isEffectiveFeesModalVisible,
        ],
    );

    /*
     ** Auxiliary salaries
     */
    const {
        isModalVisible: isAuxiliarySalaryModalVisible,
        openModal: openAuxiliarySalaryModal,
        closeModal: closeAuxiliarySalaryModal,
        initialData: initialAuxiliarySalaryData,
    } = useExtraModalState<FormDataArrayFieldWithIndex<"auxiliarySalaries">>();

    const auxiliarySalaryModal = useMemo(
        () =>
            auxiliarySalaries && (
                <AuxiliarySalaryCreateModal
                    visible={isAuxiliarySalaryModalVisible}
                    initialData={initialAuxiliarySalaryData}
                    onClose={closeAuxiliarySalaryModal}
                    onCreate={(a) => {
                        closeAuxiliarySalaryModal();
                        if (!initialAuxiliarySalaryData) {
                            auxiliarySalaries.append(a);
                        } else {
                            auxiliarySalaries.update(initialAuxiliarySalaryData.index, a);
                        }
                    }}
                />
            ),
        [
            auxiliarySalaries,
            closeAuxiliarySalaryModal,
            initialAuxiliarySalaryData,
            isAuxiliarySalaryModalVisible,
        ],
    );

    /*
     ** Absence
     */
    const {
        isModalVisible: isAbsenceModalVisible,
        openModal: openAbsenceModal,
        closeModal: closeAbsenceModal,
        initialData: initialAbsenceData,
    } = useExtraModalState<FormDataArrayFieldWithIndex<"absences">>();

    const absencesModal = useMemo(
        () =>
            absences && (
                <AbsenceCreateModal
                    visible={isAbsenceModalVisible}
                    initialData={initialAbsenceData}
                    maxDate={absences.maxDate}
                    minDate={absences.minDate}
                    onClose={closeAbsenceModal}
                    onCreate={(a) => {
                        closeAbsenceModal();
                        if (!initialAbsenceData) {
                            absences.append(a);
                        } else {
                            absences.update(initialAbsenceData.index, a);
                        }
                    }}
                />
            ),
        [absences, closeAbsenceModal, initialAbsenceData, isAbsenceModalVisible],
    );

    /*
     ** Overtime
     */
    const {
        isModalVisible: isOvertimeModalVisible,
        openModal: openOvertimeModal,
        closeModal: closeOvertimeModal,
        initialData: initialOvertimeData,
    } = useExtraModalState<number>();

    const overtimeModal = useMemo(
        () =>
            overtime && (
                <OvertimePaidCreateModal
                    visible={isOvertimeModalVisible}
                    initialData={initialOvertimeData}
                    onClose={closeOvertimeModal}
                    {...overtime}
                />
            ),
        [closeOvertimeModal, initialOvertimeData, isOvertimeModalVisible, overtime],
    );

    return {
        openBonusModal,
        bonusCreateModal,
        openEffectiveFeesModal,
        effectiveFeesModal,
        openAuxiliarySalaryModal,
        auxiliarySalaryModal,
        openAbsenceModal,
        absencesModal,
        openOvertimeModal,
        overtimeModal,
    };
};

const useExtraModalState = <T,>() => {
    const [initialData, setInitialData] = useState<T>();
    const [isModalVisible, setIsModalVisible] = useState(false);

    const openModal = useCallback((initialData?: T) => {
        setInitialData(initialData);
        setIsModalVisible(true);
    }, []);

    const closeModal = useCallback(() => {
        setIsModalVisible(false);
        setInitialData(undefined);
    }, []);

    return { isModalVisible, openModal, closeModal, initialData };
};
